import { bunja, createScope } from 'bunja';
import { atom } from 'jotai';

import { ProductType } from '@flipster/apis/v1/history/types';
import { InstrumentV2ForApiInstanceType, snapshotSpecBunja } from '@flipster/features/src/shared/snapshot/state';
import { PathnameScope } from '@flipster/react/providers/route';
import { marketsBunja } from '@flipster/socket/r230522/market';
import { pswapDetailsBunja } from '@flipster/socket/r230522/pswap-detail';
import { pswapTickersBunja } from '@flipster/socket/r230522/pswap-ticker';
import { spotDetailsBunja } from '@flipster/socket/r230522/spot-detail';
import { spotMarketsBunja } from '@flipster/socket/r230522/spot-market';
import { spotTickerBunja } from '@flipster/socket/r230522/spot-ticker';
import { JotaiStoreScope } from '@flipster/utils/bunja-jotai';

export const SymbolScope = createScope<string>();

type SymbolInfoType =
  | InstrumentV2ForApiInstanceType
  | (Partial<InstrumentV2ForApiInstanceType> & {
      pricePrecision: number;
      tradeQtyPrecision: number;
      maxLeverageHardLimit: number;
    });

export const tradeSymbolBunja = bunja(() => {
  const pathname = bunja.use(PathnameScope);
  const { instrumentsKeyBySymbolAtom } = bunja.use(snapshotSpecBunja);
  const PlaceholderSymbolInfo = { pricePrecision: 2, tradeQtyPrecision: 2, maxLeverageHardLimit: 100 };

  const symbol = pathname.split('/')[4] || 'dummy';

  const symbolInfoAtom = atom((get) => {
    const instrumentsKeyBySymbol = get(instrumentsKeyBySymbolAtom);
    return instrumentsKeyBySymbol?.[symbol];
  });

  const symbolInfoWithDefaultAtom = atom<SymbolInfoType>((get) => {
    const instrumentsKeyBySymbol = get(instrumentsKeyBySymbolAtom);
    return instrumentsKeyBySymbol?.[symbol] ?? PlaceholderSymbolInfo;
  });

  return {
    symbol,
    symbolInfoAtom,
    symbolInfoWithDefaultAtom,
  };
});

export const perpetualSymbolSocketBunja = bunja(() => {
  const store = bunja.use(JotaiStoreScope);
  const { symbol, symbolInfoAtom } = bunja.use(tradeSymbolBunja);
  const { pswapTickersAtom, setCurrentTicker } = bunja.use(pswapTickersBunja);
  const { pswapDetailsAtom, setCurrentSymbol } = bunja.use(pswapDetailsBunja);
  const { marketsAtom } = bunja.use(marketsBunja);
  const getTickerAtom = atom((get) => {
    const tickers = get(pswapTickersAtom);
    if (!tickers) return undefined;
    return tickers[symbol];
  });

  const getMarketAtom = atom((get) => {
    const markets = get(marketsAtom);
    if (!markets) return undefined;
    return markets[symbol];
  });

  const getDetailAtom = atom((get) => {
    const pswapDetails = get(pswapDetailsAtom);
    if (!pswapDetails) return undefined;
    return pswapDetails[symbol];
  });

  bunja.effect(() => {
    const symbolInfo = store.get(symbolInfoAtom);
    if (symbolInfo?.productType === ProductType.Perpetual) {
      setCurrentTicker(symbol);
      setCurrentSymbol(symbol);
    }
    return () => {};
  });

  return {
    getTickerAtom,
    getMarketAtom,
    getDetailAtom,
  };
});
perpetualSymbolSocketBunja.debugLabel = 'perpetualSymbolSocketBunja';

export const spotSymbolSocketBunja = bunja(() => {
  const store = bunja.use(JotaiStoreScope);
  const { symbol, symbolInfoAtom } = bunja.use(tradeSymbolBunja);
  const { spotTickersAtom, setCurrentTicker } = bunja.use(spotTickerBunja);
  const { spotDetailsAtom, setCurrentSymbol } = bunja.use(spotDetailsBunja);
  const { spotMarketsAtom } = bunja.use(spotMarketsBunja);
  const getTickerAtom = atom((get) => {
    const tickers = get(spotTickersAtom);
    if (!tickers) return undefined;
    return tickers[symbol];
  });

  const getMarketAtom = atom((get) => {
    const markets = get(spotMarketsAtom);
    if (!markets) return undefined;
    return markets[symbol];
  });

  const getDetailAtom = atom((get) => {
    const spotDetails = get(spotDetailsAtom);
    if (!spotDetails) return undefined;
    return spotDetails[symbol];
  });

  bunja.effect(() => {
    const symbolInfo = store.get(symbolInfoAtom);
    if (symbolInfo?.productType === ProductType.Spot) {
      setCurrentTicker(symbol);
      setCurrentSymbol(symbol);
    }
    return () => {};
  });

  return {
    getTickerAtom,
    getMarketAtom,
    getDetailAtom,
  };
});
spotSymbolSocketBunja.debugLabel = 'spotSymbolSocketBunja';
